import { getDatabase, ref as dbRef, update, remove } from 'firebase/database';
import { getDAMApp } from '../../services/firebase';
import { logger } from '../../logging';
import { clearAccountsSearchSession } from '../../components/accountsSearchSession';

/**
 * RTDB paths under user_preferences/{uid}/accounts:
 * - shared: system dropdown + showActive (AccountsContainer)
 * - tables/{tableName}: per-provider react-table + tab state (each Accounts.tsx)
 * Column visibility stays under user_preferences/.../columns/ (unchanged).
 */
export type AccountsTablePersistedPayload = {
  activeTab?: number;
  filters?: Array<{ id: string; value: unknown }>;
  sortBy?: Array<{ id: string; desc: boolean }>;
  pageIndex?: number;
  pageSize?: number;
  globalFilter?: string;
  filtersOpen?: boolean;
};

/** Everything under user_preferences/{uid}/accounts (shared + all table prefs). */
export function getAccountsRootRef(uid: string) {
  return dbRef(getDatabase(getDAMApp()), `user_preferences/${uid}/accounts`);
}

/** Persists which provider tab is selected and the Active filter toggle. */
export async function persistAccountsSharedPrefs(
  uid: string,
  payload: { system?: string; showActive?: boolean }
) {
  const r = dbRef(
    getDatabase(getDAMApp()),
    `user_preferences/${uid}/accounts/shared`
  );
  await update(r, payload);
}

/** Writes react-table + tab + optional extras under tables/{tableName}. */
export async function persistAccountsTablePrefs(
  uid: string,
  tableName: string,
  payload: AccountsTablePersistedPayload
) {
  const r = dbRef(
    getDatabase(getDAMApp()),
    `user_preferences/${uid}/accounts/tables/${tableName}`
  );
  await update(r, payload);
}

/** Drops leftover RTDB Find search so it cannot return after logout. */
export function removeLegacyAccountsSearchPreference(uid: string): void {
  if (!uid) {
    return;
  }
  const filtersFindRef = dbRef(
    getDatabase(getDAMApp()),
    `user_preferences/${uid}/filters/dd_account_search`
  );
  remove(filtersFindRef).catch((e) => {
    logger.error(`removeLegacyAccountsSearchPreference: ${e}`);
  });
}

/** Used by "Clear Filters" to drop saved Accounts prefs without touching column checkboxes. */
export async function clearAccountsFilterPreferences(uid: string) {
  const db = getDatabase(getDAMApp());
  // Legacy Find / Load Data row (previously persisted in RTDB).
  const filtersFindRef = dbRef(
    db,
    `user_preferences/${uid}/filters/dd_account_search`
  );
  clearAccountsSearchSession(uid);
  try {
    await Promise.all([
      remove(getAccountsRootRef(uid)),
      remove(filtersFindRef),
    ]);
  } catch (e) {
    logger.error(`clearAccountsFilterPreferences: ${e}`);
    throw e;
  }
}
